/**
 * Light helpers on the dom
 * @module lib/dom
 */

import { onTransitionEndOn } from './events';
import { whileMaintainingScroll } from './scroll';

const CLASS_WILL_EXIT = 'transition--willExit';
const CLASS_WILL_ENTER = 'transition--willEnter';
const CLASS_IS_EXITING = 'transition--isExiting';
const CLASS_IS_ENTERING = 'transition--isEntering';

/**
 * Removes given element from the DOM
 * @param {HTMLElement} el
 */
export function remove(el) {
  if (el && el.parentNode) {
    el.parentNode.removeChild(el);
  }
}

/**
 * Replaces `oldEl` by `newEl`, watching for `transitionend`
 * @param {HTMLElement} newEl
 * @param {HTMLElement} oldEl
 * @param {Object} options
 * @param {Function} options.beforeEnter A callback to run before the new element enters the DOM
 * @returns {Promise} A promise that resolves once the animation is complete
 */
export function replaceWithAnimation(newEl, oldEl, { beforeEnter } = {}) {
  return new Promise(resolve => {
    oldEl.classList.add(CLASS_WILL_EXIT);
    newEl.classList.add(CLASS_WILL_ENTER);
    // Wait a frame so the WILL_EXIT class has been applied
    setTimeout(() => {
      oldEl.addEventListener(
        'transitionend',
        onTransitionEndOn(oldEl, () => {
          const handleTransitionEnd = onTransitionEndOn(newEl, event => {
            removeIsExiting(event);
            resolve();
            newEl.removeEventListener('transitionend', handleTransitionEnd);
          });

          newEl.addEventListener('transitionend', handleTransitionEnd);
          whileMaintainingScroll(() => {
            oldEl.parentNode && oldEl.parentNode.replaceChild(newEl, oldEl);
          });
          beforeEnter && beforeEnter();
          setTimeout(() => {
            newEl.classList.remove(CLASS_WILL_ENTER);
            newEl.classList.add(CLASS_IS_ENTERING);
          }, 0);
        })
      );
      oldEl.classList.remove(CLASS_WILL_EXIT);
      oldEl.classList.add(CLASS_IS_EXITING);
    }, 0);
  });
}

/**
 * Removes given element
 * @param {HTMLElement} el
 * @returns {Promise} A promise that resolve with the element once the animation complete
 */
export function removeWithAnimation(el) {
  return new Promise(resolve => {
    el.classList.add(CLASS_WILL_EXIT);
    setTimeout(() => {
      el.addEventListener(
        'transitionend',
        onTransitionEndOn(el, () => {
          whileMaintainingScroll(() => {
            el.parentNode && el.parentNode.removeChild(el);
          });
          resolve(el);
        })
      );
      el.classList.remove(CLASS_WILL_EXIT);
      el.classList.add(CLASS_IS_EXITING);
    }, 0);
  });
}

/**
 * Inserts given element in the given parent waiting for animations to complete
 * @param {HTMLElement} el
 * @param {HTMLElement} parent
 * @param {Object} options
 * @param {Function} options.beforeEnter A callback that'll run before the element enters the DOM
 */
export function insertWithAnimation(el, parent, { beforeEnter } = {}) {
  return new Promise(resolve => {
    el.classList.add(CLASS_WILL_ENTER);
    parent.appendChild(el);
    const handleTransitionEnd = onTransitionEndOn(el, event => {
      removeIsExiting(event);
      resolve();
      event.target.removeEventListener('transitionend', handleTransitionEnd);
    });
    beforeEnter && beforeEnter();
    setTimeout(() => {
      el.addEventListener('transitionend', handleTransitionEnd);
      el.classList.remove(CLASS_WILL_ENTER);
    }, 0);
  });
}

/**
 * Inserts the `newNode` after the `referenceNode`
 * @param {HTMLElement} newNode
 * @param {HTMLElement} referenceNode
 */
export function insertAfter(newNode, referenceNode) {
  if (referenceNode.nextElementSibling) {
    referenceNode.parentNode.insertBefore(
      newNode,
      referenceNode.nextElementSibling
    );
  } else {
    referenceNode.parentNode.appendChild(newNode);
  }
}

function removeIsExiting(event) {
  event.target.classList.remove(CLASS_IS_ENTERING);
}



// WEBPACK FOOTER //
// ./app/webpack/javascripts/lib/dom.js